import { FC, ReactNode, useMemo, useState } from "react";

import Link from "next/link";

import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { Column, IFilter } from "@gsmvalve/gsmvalve-table";
import { t, Trans } from "@lingui/macro";
import { Image } from "antd";
import { keyBy } from "lodash";

import EmptyStates from "components/empty-states";
import DownloadModal from "components/modals/download-modal";
import Table, { mapOptionsToFilters, onHasFilters } from "components/table";

import { useApplicationContext } from "contexts/app";

import { DATA_TYPE, FILTER_CONDITION, ORDERING, Record } from "@gsmvalve/gsmvalve-table/dist/cjs/types";
import { FormQuestion, FormResponse } from "types";

import useOptions from "hooks/options";

import { getFormResponseExport } from "utils/navirec-forms";
import { extractUuidFromURL } from "utils/uuid-utils";

const filters = (basePath: string | undefined, account: string, client: any): IFilter[] => [
  {
    key: "vehicle",
    title: t`Vehicles`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}vehicles/?account=${account}&active=true`,
      label: t`Search`,
      mode: "multiple",
      valueKey: "id",
      labelKey: "name_display",
      client: client,
    },
  },
  {
    key: "driver",
    title: t`Drivers`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}drivers/?account=${account}&active=true`,
      label: t`Search`,
      mode: "multiple",
      valueKey: "id",
      labelKey: "name",
      client: client,
    },
  },
  {
    key: "created_by",
    title: t`Created by`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}users/?account=${account}`,
      label: t`Search`,
      mode: "multiple",
      valueKey: "id",
      labelKey: "name_display",
      client: client,
    },
  },
];

const modifier = (data: any) => {
  const records: Record[] = [];

  data?.forEach((rows: any) => {
    const row = rows?.data;

    const usersById = keyBy(rows?.data.users, "url");
    const driversById = keyBy(rows?.data.drivers, "url");
    const vehiclesById = keyBy(rows?.data.vehicles, "url");

    row?.form_responses?.forEach((form: any) => {
      records.push({
        ...form,
        created_by: usersById?.[form.created_by]?.name_display || "-",
        vehicle: vehiclesById?.[form.vehicle]?.name_display || "-",
        driver: driversById?.[form.driver]?.name || "-",
      });
    });
  });

  return records;
};

const EXCLUDED_QUESTIONS = ["vehicle", "driver"];
const CUSTOM_RENDER: { [key: string]: (question: FormQuestion, value: any) => ReactNode } = {
  vehicle_total_engine_time: (question, value) => {
    return (
      <div>
        {value.answers?.[question?.slug || ""]
          ? `${parseFloat(`${value.answers[question.slug as string] / 3600}`).toFixed(2)} ${t`hours`}`
          : "-"}
      </div>
    );
  },
};

const Responses: FC<{ formId: string; formQuestions: FormQuestion[] }> = ({ formId, formQuestions }) => {
  const { session, language, httpClient } = useApplicationContext();
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();

  const manageColumns = (formQuestions: FormQuestion[]) => {
    const question_columns: Column[] = formQuestions
      .filter((q) => !EXCLUDED_QUESTIONS.includes(q.question_type))
      .map((q, i) => ({
        title: q.question,
        isVisible: i < 3,
        isSortable: false,
        dataIndex: ["answers", q.slug as string],
        key: q.slug as string,
        type: DATA_TYPE.TEXT,
        ...(CUSTOM_RENDER[q.question_type] && { render: (value: any) => CUSTOM_RENDER[q.question_type](q, value) }),
      }));

    const default_columns: Column[] = [
      {
        title: t`Action`,
        fixed: true,
        isVisible: true,
        key: "action",
        width: 170,
        render(data: FormResponse) {
          return (
            <div>
              <div>
                <Link
                  target="_blank"
                  href={`/forms/${extractUuidFromURL(data.form)}/responses/${data.id}`}
                  style={{ zIndex: 1 }}
                >
                  <EyeOutlined /> <Trans>View response</Trans>
                </Link>
              </div>
              <div>
                <a
                  onClick={() =>
                    getFormResponseExport(data.id as string, language, httpClient, 'pdf').then((res) => setDownloadUrl(res))
                  }
                  style={{ zIndex: 1, marginRight: 8 }}
                >
                  <DownloadOutlined /> <Trans>PDF</Trans>
                </a>
                <a
                  onClick={() =>
                    getFormResponseExport(data.id as string, language, httpClient, 'xlsx').then((res) => setDownloadUrl(res))
                  }
                  style={{ zIndex: 1 }}
                >
                  <DownloadOutlined /> <Trans>Excel</Trans>
                </a>
              </div>
            </div>
          );
        },
      },
      {
        title: t`Created by`,
        isVisible: true,
        isSortable: false,
        dataIndex: ["created_by"],
        key: "created_by",
        render(data: FormResponse) {
          return data.created_by;
        },
      },
      {
        title: t`Created at`,
        isVisible: true,
        isSortable: true,
        key: "created_at",
        dataIndex: ["created_at"],
        type: DATA_TYPE.DATETIME,
      },
      {
        title: t`Updated at`,
        isVisible: false,
        isSortable: true,
        key: "updated_at",
        dataIndex: ["updated_at"],
        type: DATA_TYPE.DATETIME,
      },
      { key: "url", omit: true },
      { key: "form", omit: true },
    ];

    return [...default_columns, ...question_columns];
  };

  const columns = manageColumns(formQuestions);
  const url = `form_responses/?form=${formId}&sideload=users,vehicles,drivers&state=submitted&account=${session?.accountId}`;

  const { result: options } = useOptions(`form_responses/`);

  const mappedFilters = useMemo(
    () =>
      mapOptionsToFilters(options, filters(httpClient.basePath, session?.accountId as string, httpClient.swrFetcher)),
    [httpClient, options, session]
  );

  if (!formId) {
    return null;
  }

  return (
    <>
      <Table<FormResponse[]>
        key="responses"
        controls={{ manageColumn: true, filterBtn: false, quickFilter: true }}
        id={`${session?.accountId}-responses-${formId}`}
        columns={{
          filtersPath: "/form_responses/",
          path: "FormResponse",
          customColumns: columns,
          customFilters: mappedFilters,
          quickFilters: [
            { key: "vehicle", placeholder: t`Vehicle`, span: 5 },
            { key: "driver", placeholder: t`Driver`, span: 5 },
            { key: "created_by", placeholder: t`Created by`, span: 6 },
          ],
        }}
        records={{
          url,
          modifier,
          limit: 100,
          sort: [{ key: "created_at", order: "desc" as ORDERING }],
        }}
        empty={{
          onHasFilter: onHasFilters(),
          emptyWithoutFilter: (
            <EmptyStates
              title={t`No responses yet`}
              description={t`All responses will appear here.`}
              icon={<Image src={"/images/forms-empty-state.svg"} width={260} height={260} />}
            />
          ),
        }}
        offset={215}
      />
      <DownloadModal installUrl={downloadUrl} onClose={() => setDownloadUrl(undefined)} title={t`Download file`} />
    </>
  );
};

export default Responses;
